import React from "react";

// 12-level prestige configuration
// Stars 1-5: temporary (lost on de-rank)
// Elite emblems 6-12: permanent (kept forever on profile)
export const prestigeConfig: Record<
  number,
  {
    label: string;
    gradient: string;
    textColor: string;
    phase: "unranked" | "hook" | "grind" | "locked_in";
    isElite: boolean;
    /**
     * Ornate emblem art (MW3-style, served from public/prestige/).
     * When present the badge renders this PNG instead of the gradient
     * circle. Levels without art fall back to the gradient placeholder
     * until their emblem lands. Emblems are added P1 → P12 as Mako
     * generates them (P1/P2 in as of Jun 3 2026; P3 in Aug 10 2026,
     * commissioned because Alice reached Prestige III on the July crown).
     *
     * NOTE: P3 is the first TEXT-FREE emblem. P1 and P2 bake "PRESTIGE I"
     * and "PRESTIGE II" into the art. P3 is a bare silver-and-blue
     * medallion, which is what the Session 26 lock originally specified.
     * The set is now inconsistent. Mako rules which way P4+ goes.
     */
    image?: string;
  }
> = {
  0: {
    label: "Unranked",
    gradient: "from-gray-500 to-gray-600",
    textColor: "text-glass-muted",
    phase: "unranked",
    isElite: false,
  },
  1: {
    label: "Prestige 1",
    gradient: "from-amber-300 to-yellow-500",
    textColor: "text-yellow-300",
    phase: "hook",
    isElite: false,
    image: "/prestige/emblem-p1.png",
  },
  2: {
    label: "Prestige 2",
    gradient: "from-amber-400 to-yellow-600",
    textColor: "text-yellow-400",
    phase: "hook",
    isElite: false,
    image: "/prestige/emblem-p2.png",
  },
  3: {
    label: "Prestige 3",
    gradient: "from-slate-300 to-blue-700",
    textColor: "text-blue-300",
    phase: "hook",
    isElite: false,
    image: "/prestige/emblem-p3.png",
  },
  4: {
    label: "Prestige 4",
    gradient: "from-orange-400 to-amber-600",
    textColor: "text-amber-300",
    phase: "hook",
    isElite: false,
  },
  5: {
    label: "Prestige 5",
    gradient: "from-yellow-400 to-amber-500",
    textColor: "text-amber-200",
    phase: "hook",
    isElite: false,
  },
  6: {
    label: "Prestige 6",
    gradient: "from-red-500 to-red-700",
    textColor: "text-red-300",
    phase: "grind",
    isElite: true,
  },
  7: {
    label: "Prestige 7",
    gradient: "from-emerald-500 to-green-700",
    textColor: "text-green-300",
    phase: "grind",
    isElite: true,
  },
  8: {
    label: "Prestige 8",
    gradient: "from-blue-500 to-blue-700",
    textColor: "text-blue-300",
    phase: "grind",
    isElite: true,
  },
  9: {
    label: "Prestige 9",
    gradient: "from-blue-400 to-indigo-600",
    textColor: "text-indigo-300",
    phase: "grind",
    isElite: true,
  },
  10: {
    label: "Prestige 10",
    gradient: "from-purple-500 to-violet-700",
    textColor: "text-violet-300",
    phase: "grind",
    isElite: true,
  },
  11: {
    label: "Prestige 11",
    gradient: "from-pink-500 to-rose-700",
    textColor: "text-rose-300",
    phase: "locked_in",
    isElite: true,
  },
  12: {
    label: "Max Prestige",
    gradient: "from-yellow-400 via-amber-500 to-orange-600",
    textColor: "text-amber-200",
    phase: "locked_in",
    isElite: true,
  },
};

// --- Star SVG (for P1-P5) ---
const Star: React.FC<{ filled: boolean; size?: number }> = ({ filled, size = 10 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth={filled ? 0 : 2}
    className={filled ? "text-yellow-400" : "text-glass-inactive"}
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// --- Main PrestigeBadge Component ---
interface PrestigeBadgeProps {
  level: number;
  highestAchieved?: number;
  size?: "sm" | "md" | "lg" | "xl";
  showLabel?: boolean;
  showStars?: boolean;
}

const PrestigeBadge: React.FC<PrestigeBadgeProps> = ({
  level,
  highestAchieved,
  size = "md",
  showLabel = false,
  showStars = true,
}) => {
  const config = prestigeConfig[level] || prestigeConfig[0];
  const highest = highestAchieved ?? level;

  const sizeClasses = {
    sm: "w-7 h-7 text-xs",
    md: "w-10 h-10 text-sm",
    lg: "w-14 h-14 text-lg",
    xl: "w-24 h-24 text-2xl",
  };

  const starSize = { sm: 6, md: 8, lg: 12, xl: 16 };

  const hasEmblem = Boolean(config.image);

  return (
    <div className="flex items-center gap-2">
      {/* Badge */}
      <div className="relative">
        {hasEmblem ? (
          // Ornate emblem art (transparent PNG — no circle/clip, the art IS the shape)
          <img
            src={config.image}
            alt={config.label}
            className={`${sizeClasses[size]} object-contain drop-shadow-md`}
            draggable={false}
          />
        ) : (
          <div
            className={`${sizeClasses[size]} rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}
          >
            {level === 0 ? (
              <span className="font-bold text-white/60">—</span>
            ) : level <= 5 ? (
              // Stars tier: show level number
              <span className="font-bold font-mono tabular-nums text-white">{level}</span>
            ) : level === 12 ? (
              // Max prestige: crown icon
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-white"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            ) : (
              // Elite tier: show P{N}
              <span className="font-bold font-mono tabular-nums text-white text-[0.65em]">
                P{level}
              </span>
            )}
          </div>
        )}

        {/* Permanent emblem indicator (show highest achieved if de-ranked below elite) */}
        {highest >= 6 && level < highest && (
          <div
            className={`absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-br ${prestigeConfig[highest]?.gradient || ""} flex items-center justify-center border border-black/30`}
            title={`Highest: ${prestigeConfig[highest]?.label}`}
          >
            <span className="token-8 font-bold font-mono tabular-nums text-white">{highest}</span>
          </div>
        )}
      </div>

      {/* Stars display (P1-P5 only, and only for levels without ornate art —
          the emblem already encodes the rank, so the star row is redundant) */}
      {showStars && !hasEmblem && level >= 1 && level <= 5 && (
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} filled={s <= level} size={starSize[size]} />
          ))}
        </div>
      )}

      {/* Label */}
      {showLabel && (
        <div className="flex flex-col">
          <span className={`font-bold ${config.textColor} text-sm`}>{config.label}</span>
          {config.phase !== "unranked" && (
            <span className="token-10 text-glass-muted capitalize">
              {config.phase === "hook"
                ? "Rising"
                : config.phase === "grind"
                  ? "Elite"
                  : "Legendary"}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PrestigeBadge;
export { PrestigeBadge };
